import React from 'react';
import { WebsiteDesignProps } from './types';
import { Button } from "@/components/ui/button";
import { Sofa, LayoutTemplate, Palette, Ruler, Lamp, PenTool, Armchair, Home } from 'lucide-react';

const InteriorDesign: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "Studio Habitation";
    
    return (
        <div className="min-h-screen bg-[#f7f5f0] font-serif text-[#4a4a4a] selection:bg-[#c7bca5] selection:text-white border-[12px] border-white box-border relative">
            
            {/* Top Bar Branding */}
            <div className="absolute top-0 left-0 p-8 z-50">
                <span className="block font-sans text-xs font-bold uppercase tracking-[0.3em] text-[#9c9589] mb-1">Est. 1982</span>
                <h1 className="text-3xl font-normal tracking-wide italic leading-none">{schoolName}</h1>
            </div>

            {/* Right Side Nav */}
            <nav className="absolute top-0 right-0 p-8 z-50 flex flex-col items-end gap-2">
                <Button variant="ghost" className="font-sans text-xs font-bold uppercase tracking-[0.2em] hover:bg-transparent hover:text-black hover:underline underline-offset-4 px-0">
                    Projects
                </Button>
                <Button variant="ghost" className="font-sans text-xs font-bold uppercase tracking-[0.2em] hover:bg-transparent hover:text-black hover:underline underline-offset-4 px-0">
                    Studio
                </Button>
                <Button variant="ghost" className="font-sans text-xs font-bold uppercase tracking-[0.2em] hover:bg-transparent hover:text-black hover:underline underline-offset-4 px-0">
                    Faculty
                </Button>
                <Button className="mt-4 rounded-full bg-[#3d3a36] hover:bg-[#2b2926] text-[#f7f5f0] font-sans text-[10px] font-bold uppercase tracking-widest px-6 h-8">
                    Inquire
                </Button>
            </nav>

            {/* Magazine Cover Style Hero */}
            <header className="relative min-h-[90vh] flex items-center justify-center p-12">
                {/* Central Image Collage */}
                <div className="relative w-full max-w-4xl h-[60vh]">
                    {/* Main Image */}
                    <div className="absolute top-0 left-10 w-3/4 h-full bg-stone-300 shadow-2xl z-10 overflow-hidden">
                        <img 
                            src="https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?q=80&w=2000&auto=format&fit=crop" 
                            alt="Interior Space" 
                            className="w-full h-full object-cover grayscale-[20%] hover:grayscale-0 transition-all duration-1000 transform hover:scale-105"
                        />
                    </div>
                    {/* Secondary Image Overlap */}
                    <div className="absolute bottom-[-40px] right-0 w-1/3 h-2/3 bg-stone-200 shadow-xl z-20 border-[8px] border-[#f7f5f0] overflow-hidden">
                        <img 
                            src="https://images.unsplash.com/photo-1586023492125-27b2c045efd7?q=80&w=2158&auto=format&fit=crop" 
                            alt="Furniture Detail" 
                            className="w-full h-full object-cover"
                        />
                    </div>
                    
                    {/* Floating Text */}
                    <div className="absolute top-[20%] left-[-20px] z-30 bg-[#f7f5f0]/90 backdrop-blur-sm p-6 shadow-sm max-w-xs">
                        <h2 className="text-4xl font-normal leading-tight mb-4">
                            Defining <br/>
                            <span className="italic text-[#8c8270]">Spaces.</span>
                        </h2>
                        <p className="font-sans text-xs font-medium text-[#6b665e] leading-relaxed">
                            A curated curriculum for the modern interior architect. Focusing on materiality, light, and human-centric design.
                        </p>
                    </div>
                </div>
            </header>

            {/* Curriculum Grid */}
            <section className="py-24 px-12 md:px-24 bg-white border-t border-[#f0ebe0]">
                <div className="max-w-6xl mx-auto">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
                        {[
                            { icon: LayoutTemplate, title: "Spatial Planning", desc: "Form & Function" },
                            { icon: Palette, title: "Color Theory", desc: "Mood & Psychology" },
                            { icon: Lamp, title: "Lighting Design", desc: "Atmosphere Control" },
                            { icon: Sofa, title: "Furniture", desc: "Custom Fabrication" }
                        ].map((item, i) => (
                            <div key={i} className="flex flex-col items-center text-center group">
                                <div className="w-16 h-16 rounded-full border border-[#e0dcd0] flex items-center justify-center text-[#8c8270] mb-6 group-hover:bg-[#f7f5f0] transition-colors">
                                    <item.icon strokeWidth={1} size={28} />
                                </div>
                                <h3 className="text-lg font-normal tracking-wide mb-2">{item.title}</h3>
                                <div className="w-8 h-px bg-[#e0dcd0] mb-3"></div>
                                <p className="font-sans text-xs uppercase tracking-widest text-[#9c9589]">{item.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

             {/* Footer */}
            <footer className="bg-[#3d3a36] text-[#ccbca5] py-20 px-12 text-center">
                 <div className="max-w-2xl mx-auto">
                     <span className="font-serif italic text-3xl block mb-8">{schoolName}</span>
                     
                     <div className="grid grid-cols-3 gap-8 mb-12 font-sans text-[10px] font-bold uppercase tracking-[0.2em] text-[#8f887a]">
                         <a href="#" className="hover:text-white transition-colors">Catalog</a>
                         <a href="#" className="hover:text-white transition-colors">Showroom</a>
                         <a href="#" className="hover:text-white transition-colors">Careers</a>
                     </div>
                     
                     <p className="font-sans text-[10px] text-[#5e5a52]">
                         Accredited by the Council for Interior Design Accreditation. <br/>
                         © 2026. Design is intelligence made visible.
                     </p>
                 </div>
            </footer>
        </div>
    );
};

export default InteriorDesign;
